/**
 * Expanded Question Bank: Domain 7 — Practical & Social Reasoning (الاستدلال العملي والاجتماعي)
 * 15 Balanced Professional & Dilemma Templates with Strict Equal-Length Options
 */
function makeSocialStimulus(line1_ar, line2_ar, line1_en, line2_en) {
  const stimulusHtml_ar = `
    <div class="iq-stimulus-card">
      <div class="iq-stimulus-card-subtitle">${line1_ar}</div>
      <div class="iq-stimulus-card-highlight">${line2_ar}</div>
    </div>
  `;
  const stimulusHtml_en = `
    <div class="iq-stimulus-card">
      <div class="iq-stimulus-card-subtitle">${line1_en}</div>
      <div class="iq-stimulus-card-highlight">${line2_en}</div>
    </div>
  `;
  return { stimulusHtml: stimulusHtml_ar, stimulusHtml_ar, stimulusHtml_en };
}

export const EXPANDED_SOCIAL_TEMPLATES = [
  // 1. Psychological Safety & Blameless Post-Mortem
  {
    id: 'social_blameless_postmortem',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const correctAns = {
        ar: 'عقد جلسة مراجعة خالية من اللوم تركز على سد الثغرات الإجرائية وتحديث أدوات التحقق التلقائية',
        en: 'Facilitate a blameless post-mortem focused on closing procedural gaps and updating automated verification tooling'
      };
      const distractors = [
        {
          ar: 'توجيه لفت نظر رسمي للموظف المتسبب ليكون رادعاً لبقية أعضاء الفريق وضمان عدم تكرار الهفوة',
          en: 'Issue a formal disciplinary warning to the responsible engineer to establish deterrence and prevent repeat lapses'
        },
        {
          ar: 'إلزام جميع المطورين بطلب موافقة كتابية مسبقة من الإدارة العليا قبل إجراء أي تعديل مستقبلي',
          en: 'Mandate that all developers obtain prior written executive approval before deploying any future code adjustments'
        },
        {
          ar: 'التكتم التام على الحادثة وتجاوز الأمر سريعاً لتفادي إحراج الموظف أمام زملائه في القسم التقني',
          en: 'Conceal the incident entirely and move on quickly to prevent embarrassing the employee before technical colleagues'
        }
      ];

      const allChoices = prng.shuffle([
        { ...correctAns, isCorrect: true },
        ...distractors.map(d => ({ ...d, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stim = makeSocialStimulus(
        'الواقعة: تسبب تعديل غير مقصود من مهندس في توقف مؤقت للخدمة،',
        'وقد اعترف المهندس بمسؤوليته فوراً وعمل مع الفريق لإصلاح الخلل.',
        'Incident: An unintended code change caused a temporary service outage,',
        'and the engineer promptly took ownership and worked with the team to fix the issue.'
      );

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هو التصرف القيادي الأكثر نضجاً وفاعلية لبناء ثقافة عمل مرنة ومستدامة تمنع تكرار الحادث؟`,
        questionText_en: `What is the most constructive leadership action to foster systemic resilience without discouraging transparency?`,
        ...stim,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `المراجعة بدون لوم (Blameless Post-Mortem) تبحث في الخلل الهيكلي للنظام بدلاً من لوم الفرد، مما يحافظ على الشفافية والأمان النفسي.`,
        explanation_en: `Blameless reviews inspect systemic safeguards rather than penalizing honesty, reinforcing institutional psychological safety.`
      };
    }
  },

  // 2. Cross-Functional Scope Creep
  {
    id: 'social_scope_creep_negotiation',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const correctAns = {
        ar: 'الترحيب بالمقترح مع توضيح أثره الزمني وتقديم خيارين: تأجيل موعد الإطلاق أو ترحيل ميزة سابقة',
        en: 'Welcome the idea while clarifying scheduling impacts, offering choices between deadline extension or feature trade-offs'
      };
      const distractors = [
        {
          ar: 'الرفض القاطع للمقترح بصورة فورية مع التذكير الصارم بالاتفاق التعاقدي الأصلي المغلق للمشروع',
          en: 'Flatly reject the proposal immediately citing strict adherence to the project originally finalized scope'
        },
        {
          ar: 'الموافقة الفورية والضغط على فريق التطوير للعمل ساعات إضافية مكثفة لإنهاء كل شيء في الموعد',
          en: 'Accept unconditionally and pressure engineering into mandatory overtime to deliver everything on the initial deadline'
        },
        {
          ar: 'تجاهل طلبات الشريك والتركيز حصراً على الخطة السابقة وإطلاعه على النتيجة فقط في يوم التدشين',
          en: 'Ignore partner requests entirely, executing the original plan and unveiling deliverables strictly on launch day'
        }
      ];

      const allChoices = prng.shuffle([
        { ...correctAns, isCorrect: true },
        ...distractors.map(d => ({ ...d, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stim = makeSocialStimulus(
        'الموقف: طلب شريك العمل ميزات جوهرية جديدة قبل أسبوعين فقط',
        'من موعد الإطلاق المحدد، مما يهدد الجدول الزمني العام للخطة.',
        'Situation: A partner requested major new features just two weeks prior',
        'to the scheduled launch, jeopardizing the project delivery timeline.'
      );

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `كيف يجب على مدير المشروع إدارة هذا الطلب المفاجئ لتحقيق التوازن بين الشراكة وجودة التسليم؟`,
        questionText_en: `How should the project manager handle this last-minute request to balance partnership value with delivery feasibility?`,
        ...stim,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `إدارة النطاق الناجحة تركز على المفاضلة الواعية (Trade-offs) بشفافية بدلاً من الصدام العقيم أو التضحية بسلامة الفريق.`,
        explanation_en: `Effective scope management presents explicit trade-offs rather than aggressive resistance or unfeasible commitments.`
      };
    }
  },

  // 3. Technical Debt Prioritization Dilemma
  {
    id: 'social_tech_debt_prioritization',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const correctAns = {
        ar: 'تخصيص نسبة مئوية ثابتة من طاقة الفريق في كل دورة عمل لإصلاح الديون التقنية تدريجياً بالتوازي',
        en: 'Allocate a fixed percentage of sprint capacity continuously to refactor technical debt alongside new features'
      };
      const distractors = [
        {
          ar: 'إيقاف كافة ميزات المنتج الجديدة تماماً لعدة أشهر متتالية للتفرغ الحصري لإعادة البناء البرمجي',
          en: 'Halt all product feature development for several months to focus exclusively on architectural refactoring'
        },
        {
          ar: 'تأجيل معالجة المشكلات البرمجية الداخلية حتى ينهار النظام فعلياً لتبرير الحاجة للإصلاح أمام الإدارة',
          en: 'Postpone code improvements until systems fail completely to visually justify remediation budgets to executives'
        },
        {
          ar: 'مضاعفة وتيرة إطلاق الميزات التسويقية وتجاهل شكاوى الفريق الفني باعتبارها هواجس هندسية مبالغة',
          en: 'Accelerate feature rollouts while dismissing technical debt complaints as purely exaggerated engineering purism'
        }
      ];

      const allChoices = prng.shuffle([
        { ...correctAns, isCorrect: true },
        ...distractors.map(d => ({ ...d, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stim = makeSocialStimulus(
        'التحدي: تراكم الديون البرمجية القديمة يبطئ سرعة الفريق التقني،',
        'بينما تطالب إدارة التسويق بمواصلة وتيرة إطلاق ميزات جديدة.',
        'Challenge: Accumulated legacy technical debt impedes development velocity,',
        'while product marketing demands accelerating new feature releases.'
      );

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هي الاستراتيجية المستدامة التي تضمن استقرار البنية التحتية مع مواصلة تقديم قيمة تجارية مستمرة؟`,
        questionText_en: `Which sustainable product strategy balances continuous architectural health with progressive commercial output?`,
        ...stim,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `تخصيص حصة منتظمة (مثل 15-20% من كل Sprint) يحقق تحسيناً مستمراً دون إيقاف الأعمال أو المخاطرة بانهيار المنظومة.`,
        explanation_en: `Dedicating a fixed capacity slice to refactoring enables continuous resilience without halting business velocity.`
      };
    }
  },

  // 4. Constructive Upward Feedback to Leadership
  {
    id: 'social_upward_feedback',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const correctAns = {
        ar: 'طلب اجتماع فردي خاص مع المدير وعرض أمثلة موضوعية موثقة حول كيفية تأثير أسلوبه في وتيرة العمل',
        en: 'Request a private 1-on-1 meeting presenting objective documented examples of how microscopic oversight delays delivery'
      };
      const distractors = [
        {
          ar: 'طرح الانتقادات بصوت مرتفع خلال الاجتماع العام لجمع تأييد الزملاء وإحراج المدير لدفعه للتراجع',
          en: 'Voice frustrations openly during all-hands meetings to rally peer support and pressure management publicly'
        },
        {
          ar: 'الامتثال الصامت لكافة الأوامر مع تقليص المبادرة الذاتية وترك المهام تتعطل لإثبات فشل طريقة إدارته',
          en: 'Silently comply with all micro-tasks while withholding initiative to let bottlenecks demonstrate managerial flaws'
        },
        {
          ar: 'إرسال شكوى مجهولة المصدر للموارد البشرية للمطالبة بعزل المدير دون الحديث المباشر معه إطلاقاً',
          en: 'Submit an anonymous grievance to human resources demanding managerial reassignment without direct dialogue'
        }
      ];

      const allChoices = prng.shuffle([
        { ...correctAns, isCorrect: true },
        ...distractors.map(d => ({ ...d, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stim = makeSocialStimulus(
        'الموقف: يمارس المدير تدقيقاً مفرطاً وتدخلاً في أدق التفاصيل اليومية',
        '(Micromanagement)، مما يحد من إبداع الفريق ويبطئ تسليم المهام.',
        'Situation: A manager exercises persistent micromanagement in daily tasks,',
        'constraining team initiative and delaying project deliverables.'
      );

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هو المسار المهني الأكثر نضجاً لمعالجة هذه المعضلة وتحسين بيئة العمل دون صدام شخصي سلبي؟`,
        questionText_en: `What is the most constructive professional approach to address micromanagement and restore autonomy?`,
        ...stim,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `التغذية الراجعة الصاعدة (Upward Feedback) تنجح عبر الحوار الفردي الخاص والتركيز على أثر السلوك في نتائج العمل الموضوعية.`,
        explanation_en: `Constructive upward feedback requires private dialogue centered on objective impact rather than emotional confrontation.`
      };
    }
  },

  // 5. Whistleblower & Ethical Compliance Dilemma
  {
    id: 'social_ethical_whistleblower',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const correctAns = {
        ar: 'توثيق الخلل كتابياً وإبلاغ لجنة الامتثال الداخلي وحماية البيانات وفق القنوات القانونية المعتمدة',
        en: 'Document the vulnerability in writing and escalate through the approved internal data governance and compliance channels'
      };
      const distractors = [
        {
          ar: 'نشر تفاصيل الثغرة الأمنية على وسائل التواصل الاجتماعي فوراً لإجبار الشركة على التصرف بحزم',
          en: 'Disclose vulnerability details publicly on social networks immediately to force corporate remediation under scrutiny'
        },
        {
          ar: 'الرضوخ الكامل لضغوط المدير وحذف سجلات الفحص لحماية مستقبلك الوظيفي وتجنب أي خلاف إداري',
          en: 'Yield to managerial pressure and erase diagnostic logs to protect personal job security and avoid friction'
        },
        {
          ar: 'استغلال الثغرة سراً للحصول على نسخ من البيانات واستخدامها كورقة ضغط في مفاوضات الترقية المستقبلية',
          en: 'Exploit the flaw privately to extract data records to leverage as bargaining power during compensation reviews'
        }
      ];

      const allChoices = prng.shuffle([
        { ...correctAns, isCorrect: true },
        ...distractors.map(d => ({ ...d, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stim = makeSocialStimulus(
        'المعضلة: اكتشفت ثغرة خصوصية تعرض بيانات العملاء للخطر، لكن',
        'المدير طلب تجاهلها مؤقتاً لتجنب تأخير الإطلاق وإلحاق ضرر بالأرباح.',
        'Dilemma: A privacy flaw was discovered exposing customer records, but',
        'leadership requested ignoring it to avoid release delays and revenue loss.'
      );

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هو التصرف الأخلاقي والمهني السليم الذي يوفق بين النزاهة القانونية والمسؤولية المهنية؟`,
        questionText_en: `What is the ethical and legally defensible course of action to uphold institutional integrity?`,
        ...stim,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `المسؤولية الأخلاقية والقانونية تلزم الموظف بتوثيق المخاطر وإبلاغ الجهات الرقابية المعنية داخلياً لحماية خصوصية المستخدمين.`,
        explanation_en: `Ethical integrity dictates documented escalation via compliance oversight channels to safeguard privacy and adherence to law.`
      };
    }
  },

  // 6. Asynchronous Remote Work Communication
  {
    id: 'social_async_remote_alignment',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const correctAns = {
        ar: 'صياغة وثائق تخطيط واضحة ومكتوبة تحدد السياق والقرارات المطلوبة بدقة لتقليل الحاجة للاجتماعات',
        en: 'Author clear asynchronous documentation outlining context and required decisions to minimize meeting dependency'
      };
      const distractors = [
        {
          ar: 'إلزام جميع الموظفين في المناطق الزمنية البعيدة بالبقاء مستيقظين ليلاً لحضور الاجتماعات المباشرة',
          en: 'Mandate that distributed team members in distant time zones remain awake late to attend live conference calls'
        },
        {
          ar: 'الاعتماد الحصري على الرسائل النصية القصيرة الفورية المتفرقة دون أي سجلات أو ملخصات مرجعية',
          en: 'Rely solely on sporadic ad-hoc instant chat messaging without centralized reference summaries or records'
        },
        {
          ar: 'إلغاء التنسيق بين الفرق المتفرقة وجعل كل فريق يعمل بمعزل تام عن الأهداف الكبرى للمؤسسة',
          en: 'Abolish inter-team coordination entirely, letting regional branches operate isolated from corporate strategy'
        }
      ];

      const allChoices = prng.shuffle([
        { ...correctAns, isCorrect: true },
        ...distractors.map(d => ({ ...d, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stim = makeSocialStimulus(
        'الموقف: يعمل الفريق التقني عن بعد عبر ثلاث قارات مختلفة',
        'بفوارق زمنية تفوق 8 ساعات، مما يجعل الاجتماعات المتزامنة مرهقة.',
        'Situation: A remote engineering team operates across three continents',
        'with 8+ hour time zone gaps, making live meetings unsustainable.'
      );

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هي أفضل ممارسة تنظيمية لضمان التنسيق السلس والإنتاجية العالية عبر المناطق الزمنية المختلفة؟`,
        questionText_en: `What is the most effective operational framework to maintain distributed velocity across divergent time zones?`,
        ...stim,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `التواصل غير المتزامن (Async-first) المعتمد على التوثيق المكتوب الواضح هو الركيزة الأساسية لنجاح الفرق العالمية الموزعة.`,
        explanation_en: `Asynchronous documentation-first culture allows continuous progress without forcing unsustainable schedule disruption.`
      };
    }
  },

  // 7. Team Burnout & Sustainable Pace
  {
    id: 'social_team_burnout_mitigation',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const correctAns = {
        ar: 'مراجعة أولويات المشروع وإلغاء المهام غير الأساسية وإعادة ضبط الالتزامات بما يضمن وتيرة عمل مستدامة',
        en: 'Reassess backlog priorities, trim non-essential scope, and recalibrate milestone dates to ensure sustainable pace'
      };
      const distractors = [
        {
          ar: 'تنظيم حفل ترفيهي مسائي سريع خارج أوقات الدوام لرفع المعنويات مع الإبقاء على نفس ضغط العمل',
          en: 'Host an evening social gathering outside working hours to boost morale while maintaining identical deadline workloads'
        },
        {
          ar: 'استبدال الموظفين المنهكين بكوادر جديدة أكثر قدرة على العمل تحت الضغط المتواصل دون اعتراض',
          en: 'Replace exhausted personnel with fresh junior recruits willing to endure continuous intense overtime without complaint'
        },
        {
          ar: 'حث الفريق على بذل مزيد من الصبر ومضاعفة شرب المنبهات لاجتياز الأزمة الحالية دون تخفيض المهام',
          en: 'Urge team members to display endurance and consume more caffeine to power through without trimming targets'
        }
      ];

      const allChoices = prng.shuffle([
        { ...correctAns, isCorrect: true },
        ...distractors.map(d => ({ ...d, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stim = makeSocialStimulus(
        'المؤشرات: لوحظ تراجع في حماس الفريق وارتفاع في الأخطاء والغياب',
        'بسبب العمل لساعات إضافية متواصلة لثلاثة أشهر متتالية.',
        'Signals: Team morale dropped with rising defect rates and absenteeism',
        'due to continuous overtime workloads over three consecutive months.'
      );

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هو التدخل الإداري الحقيقي والجذري لعلاج ظاهرة الاحتراق المهني (Burnout) لدى الفريق؟`,
        questionText_en: `What is the most effective operational intervention to cure systemic team burnout at its root cause?`,
        ...stim,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `علاج الاحتراق يتطلب معالجة السبب الجذري المتمثل في ضغط النطاق والجدول الزمني عبر إعادة ترتيب الأولويات بصورة واقعية.`,
        explanation_en: `Mitigating burnout requires addressing underlying structural root causes by trimming unviable scope commitments.`
      };
    }
  },

  // 8. Credit Attribution & Contribution Recognition
  {
    id: 'social_credit_attribution',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const correctAns = {
        ar: 'توضيح دور الزميل المبتكر علناً خلال الاجتماع وشكره على فكرته الأصلية قبل استكمال عرض التفاصيل',
        en: 'Acknowledge the colleague original concept openly during the presentation before elaborating on execution details'
      };
      const distractors = [
        {
          ar: 'نسب الفكرة بالكامل لنفسك أمام الإدارة العليا باعتبار أن الجهد الحقيقي يكمن في إعداد العرض التقديمي',
          en: 'Claim sole ownership of the initiative since preparing the visual presentation slide deck required your effort'
        },
        {
          ar: 'الاعتذار للزميل سراً على انفراد بعد انتهاء العرض دون الإفصاح عن مساهمته أمام مسؤولي الشركة',
          en: 'Apologize to the colleague privately afterwards while withholding public acknowledgment before company executives'
        },
        {
          ar: 'إلغاء العرض التقديمي والانسحاب من المشروع تماماً لتفادي أي حرج أدبي قد يطرأ مع زميلك في العمل',
          en: 'Cancel the presentation and withdraw from the initiative altogether to avoid interpersonal awkwardness with the peer'
        }
      ];

      const allChoices = prng.shuffle([
        { ...correctAns, isCorrect: true },
        ...distractors.map(d => ({ ...d, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stim = makeSocialStimulus(
        'الموقف: طلب منك تقديم فكرة مشروع واعدة أمام الإدارة، لكن الفكرة',
        'الأساسية كانت نتاج نقاش غير رسمي طرحه زميلك في وقت سابق.',
        'Situation: You are asked to present a promising project concept to executives,',
        'though the foundational insight came from an informal peer conversation.'
      );

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هو السلوك المهني الأخلاقي الذي يعزز الثقة المشتركة ويبني سمعة احترافية رصينة؟`,
        questionText_en: `What is the most ethically integral and collaborative approach to credit attribution in public settings?`,
        ...stim,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `إسناد الفضل لأصحابه علناً يعزز الثقة بين الزملاء ويظهرك كقائد متعاون وناضج يحترم مساهمات الآخرين.`,
        explanation_en: `Publicly giving credit fosters mutual psychological safety and highlights collaborative leadership maturity.`
      };
    }
  },

  // 9. Change Management & Stakeholder Resistance
  {
    id: 'social_change_management_resistance',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const correctAns = {
        ar: 'إشراك المعترضين في ورش عمل استشارية لفهم مخاوفهم وتصميم برامج تدريبية تدريجية تسهم في تمكينهم',
        en: 'Engage dissenting users in consultative workshops to address friction points and design hands-on transitional training'
      };
      const distractors = [
        {
          ar: 'فرض النظام الجديد بصورة مفاجئة وإلغاء صلاحيات الوصول إلى النظام القديم دون تقديم أي دعم فني',
          en: 'Impose the new platform abruptly while cutting legacy system access without offering any transition support'
        },
        {
          ar: 'إلغاء خطة التحول الرقمي بالكامل والعودة للأساليب اليدوية القديمة تجنباً لإثارة استياء الموظفين',
          en: 'Abandon digital transformation plans entirely and revert to legacy manual workflows to avert personnel complaints'
        },
        {
          ar: 'توجيه عقوبات تأديبية فورية لكل من يبدي تحفظاً على وتيرة التحول لإجبار الجميع على الطاعة السريعة',
          en: 'Issue immediate disciplinary penalties against any staff expressing doubts to coerce rapid uncritical compliance'
        }
      ];

      const allChoices = prng.shuffle([
        { ...correctAns, isCorrect: true },
        ...distractors.map(d => ({ ...d, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stim = makeSocialStimulus(
        'التحدي: قوبل إدخال نظام تقني جديد بمقاومة واسعة وتخوف من موظفي',
        'العمليات من تعقيد الإجراءات أو فقدان السيطرة على أعمالهم اليومية.',
        'Challenge: Launching a new operational platform met widespread staff friction',
        'over procedural complexity and fear of losing control over daily workflows.'
      );

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هي منهجية إدارة التغيير الأكثر فاعلية لضمان نجاح التحول الرقمي وتبني المستخدمين للنظام؟`,
        questionText_en: `What is the most effective change management strategy to turn user apprehension into successful adoption?`,
        ...stim,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `مقاومة التغيير تعالج بالاحتواء والتمكين والمشاركة في وضع الحلول وليس بالفرض القسري أو الاستسلام للإحباط.`,
        explanation_en: `Resistance is best mitigated through empathetic engagement, targeted enablement, and collaborative ownership.`
      };
    }
  },

  // 10. Agitated Client Conflict De-escalation
  {
    id: 'social_customer_deescalation',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const correctAns = {
        ar: 'الاستماع النشط لشكوى العميل والاعتراف بأثر المشكلة على أعماله مع تقديم خطة عمل فورية محددة الخطوات',
        en: 'Listen actively, validate the business impact empathetically, and present a structured remediation action plan with milestones'
      };
      const distractors = [
        {
          ar: 'مقاطعة العميل فوراً لتوضيح أن الخطأ ناتج عن سوء استخدامه هو للنظام وتبرئة الشركة من أي مسؤولية',
          en: 'Interrupt the client defensively to point out their user errors and absolve company infrastructure from liability'
        },
        {
          ar: 'قطع الاتصال فوراً والامتناع عن الرد حتى يهدأ غضب العميل ويتحدث بأسلوب أكثر هدوءاً في يوم آخر',
          en: 'Terminate the call abruptly and ignore future tickets until the client calms down and contacts support respectfully'
        },
        {
          ar: 'تقديم وعود فورية غير واقعية بإعادة كامل المبلغ مع تعويض مالي باهظ دون الرجوع للسياسات المعتمدة',
          en: 'Make unrealistic promises of massive financial refunds immediately without reviewing organizational policies or terms'
        }
      ];

      const allChoices = prng.shuffle([
        { ...correctAns, isCorrect: true },
        ...distractors.map(d => ({ ...d, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stim = makeSocialStimulus(
        'الموقف: اتصل عميل رئيسي وهو في حالة غضب شديد بسبب انقطاع الخدمة',
        'عن متجره الإلكتروني خلال ساعات الذروة، مهدداً برفع دعوى قضائية.',
        'Situation: A key enterprise customer calls in intense distress following an outage',
        'during peak store traffic, threatening immediate legal escalation.'
      );

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هي الخطوة المهنية الأولى والأكثر فاعلية لامتصاص غضب العميل واستعادة الثقة التعاقدية؟`,
        questionText_en: `What is the most effective professional protocol to de-escalate customer distress and rebuild confidence?`,
        ...stim,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `تهدئة النزاع تبدأ بالاستماع التعاطفي وتأكيد تفهم أثر المشكلة، ثم تقديم حلول عملية واضحة ومجدولة زمنياً.`,
        explanation_en: `Conflict resolution begins with empathetic active listening followed by concrete, accountable action commitments.`
      };
    }
  },

  // 11. Vendor Negotiation Deadlock
  {
    id: 'social_vendor_negotiation_winwin',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const correctAns = {
        ar: 'البحث عن بنود متبادلة غير مالية كطول مدة العقد أو تقديم خدمات استشارية للوصول لاتفاق متوازن',
        en: 'Explore non-monetary trade-offs such as extended contract terms or reciprocal services to achieve mutually balanced value'
      };
      const distractors = [
        {
          ar: 'الانسحاب الصاخب من المفاوضات وتوجيه إهانات للمورد لدفعه للتراجع عن أسعاره خوفاً من خسارة الصفقة',
          en: 'Rupture negotiations aggressively with threats to coerce the vendor into price cuts under fear of deal termination'
        },
        {
          ar: 'الموافقة على شروط المورد المرتفعة بالكامل دون أي نقاش وتحميل الخسائر لميزانية قسم التطوير الداخلي',
          en: 'Accept vendor inflated pricing terms unconditionally and absorb the resulting deficits within internal budgets'
        },
        {
          ar: 'تسريب عروض الأسعار السرية للمورد للمنافسين لإحراجه وإجباره على تقديم تخفيضات غير مسبوقة',
          en: 'Leak the vendor proprietary pricing proposals to competitors to publicly embarrass them into unprecedented discounts'
        }
      ];

      const allChoices = prng.shuffle([
        { ...correctAns, isCorrect: true },
        ...distractors.map(d => ({ ...d, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stim = makeSocialStimulus(
        'الموقف: وصلت المفاوضات مع مورد بنية تحتية سحابية إلى طريق مسدود',
        'بشأن السعر السنوي، في حين تحتاج شركتك لخدماته الحيوية بشكل ملح.',
        'Situation: Pricing talks with a critical cloud provider hit a deadlock,',
        'while your organization urgently requires their infrastructure.'
      );

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `كيف يمكن كسر هذا الجمود التفاوضي وتحقيق مكسب مشترك (Win-Win) يحافظ على العلاقة الاستراتيجية؟`,
        questionText_en: `How can this negotiation deadlock be broken to achieve a constructive win-win agreement?`,
        ...stim,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `المفاوضات الناضجة توسع دائرة القيمة بالبحث عن عناصر بديلة (كشروط الدفع أو مدة التعاقد) تتجاوز الصراع حول السعر المجرد.`,
        explanation_en: `Principled negotiation expands value by trading non-monetary levers rather than engaging in positional bargaining.`
      };
    }
  },

  // 12. Strategic Resource Allocation Under Crisis
  {
    id: 'social_crisis_resource_allocation',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const correctAns = {
        ar: 'حماية الخدمات الأساسية الحيوية للعملاء أولاً وتأجيل المشاريع التجريبية الجديدة مؤقتاً لحين انتهاء الأزمة',
        en: 'Protect mission-critical core customer services first while temporarily pausing exploratory experimental initiatives'
      };
      const distractors = [
        {
          ar: 'تخفيض ميزانيات كافة الأقسام والمشاريع بنسبة مئوية متساوية وموحدة دون مراعاة لأهمية أي مشروع',
          en: 'Enact flat uniform percentage budget cuts across all departments without assessing distinct strategic priorities'
        },
        {
          ar: 'توجيه كل الموارد المالية المتبقية للحملات الإعلانية الصاخبة لإخفاء بوادر الأزمة عن الجمهور والمستثمرين',
          en: 'Channel remaining liquid reserves into aggressive advertising campaigns to conceal organizational distress from markets'
        },
        {
          ar: 'إغلاق الأقسام التشغيلية بالكامل والاستمرار في الإنفاق على برامج المكافآت الترفيهية للإدارة التنفيذية',
          en: 'Shut down core operating units while maintaining full executive bonus stipends and discretionary allowances'
        }
      ];

      const allChoices = prng.shuffle([
        { ...correctAns, isCorrect: true },
        ...distractors.map(d => ({ ...d, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stim = makeSocialStimulus(
        'الموقف: واجهت الشركة انخفاضاً حاداً ومفاجئاً في التمويل بنسبة 35%،',
        'مما يفرض ترشيداً سريعاً للمصروفات مع الحفاظ على استمرارية العمل.',
        'Situation: The organization faces an unexpected 35% funding contraction,',
        'demanding immediate budget triage while safeguarding core operations.'
      );

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هو المعيار الاستراتيجي الأكثر رشداً ومسؤولية لتوزيع الموارد المتبقية أثناء الأزمة؟`,
        questionText_en: `What is the most disciplined and responsible resource triage principle during acute budget retrenchment?`,
        ...stim,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `إدارة الأزمات تقتضي ترتيب الأولويات على أساس الأثر الحيوي (Triage) لحماية النواة التشغيلية الأساسية قبل التوسع.`,
        explanation_en: `Crisis triage mandates safeguarding indispensable operating core functions ahead of speculative bets.`
      };
    }
  },

  // 13. Data Privacy Incident Transparency
  {
    id: 'social_privacy_incident_transparency',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const correctAns = {
        ar: 'إخطار المستخدمين والجهات التنظيمية بوضوح وتفصيل البيانات المتأثرة مع تقديم خطوات لحماية حساباتهم',
        en: 'Notify affected users and regulators transparently detailing the breach scope alongside actionable protective steps'
      };
      const distractors = [
        {
          ar: 'إنكار وقوع أي اختراق أمني في وسائل الإعلام والتكتم على الواقعة لحماية سمعة العلامة التجارية وقيمتها',
          en: 'Deny the security compromise across news media and bury forensic records to insulate brand market capitalization'
        },
        {
          ar: 'الانتظار عدة أشهر حتى تكتمل التحقيقات الجنائية بشكل تام قبل إبلاغ أي من العملاء المعرضين للسرقة',
          en: 'Wait multiple months for judicial inquiries to conclude fully before notifying vulnerable end users of danger'
        },
        {
          ar: 'إلقاء اللوم على المستخدمين بادعاء أن كلمات مرورهم كانت ضعيفة وإخلاء مسؤولية الشركة التقنية تماماً',
          en: 'Blame impacted users by claiming their passwords were too weak, absolving corporate engineering of negligence'
        }
      ];

      const allChoices = prng.shuffle([
        { ...correctAns, isCorrect: true },
        ...distractors.map(d => ({ ...d, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stim = makeSocialStimulus(
        'الموقف: تأكد حدوث تسريب أمني محدود لبعض عناوين البريد الإلكتروني',
        'للعملاء نتيجة ثغرة تم ترقيعها فوراً دون تسريب أي كلمات مرور.',
        'Situation: A limited data disclosure exposed customer email addresses',
        'via a promptly patched flaw, with zero authentication credentials leaked.'
      );

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هو الالتزام الأخلاقي والقانوني السليم الذي يجب على إدارة الشركة اتخاذه فوراً تجاه المتضررين؟`,
        questionText_en: `What is the legally compliant and ethical communication protocol to execute following a data breach?`,
        ...stim,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `الشفافية الفورية وإشعار المتضررين والجهات الرقابية هو السبيل الوحيد لبناء الثقة والامتثال للتشريعات العالمية الصارمة.`,
        explanation_en: `Timely disclosure and transparent consumer advisory constitute the baseline for regulatory and ethical compliance.`
      };
    }
  },

  // 14. Mentorship & Junior Developer Empowerment
  {
    id: 'social_mentorship_empowerment',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const correctAns = {
        ar: 'توجيه الموظف بالأسئلة الاستكشافية ومشاركته في التفكير المنطقي لتمكينه من اكتشاف الحل بنفسه وتطوير مهاراته',
        en: 'Guide the junior engineer with probing questions, encouraging logical reflection to discover solutions autonomously'
      };
      const distractors = [
        {
          ar: 'أخذ لوحة المفاتيح منه وإنجاز المهمة بدلاً عنه بسرعة لتفادي إضاعة الوقت في الشرح والتوضيح النظري',
          en: 'Seize the keyboard and implement code changes directly on their behalf to avoid spending time mentoring'
        },
        {
          ar: 'توبيخ الموظف على عدم معرفته بالحل وإلزامه بالبحث المنفرد دون تقديم أي إرشاد أو تلميح منهجي',
          en: 'Reprimand the junior for missing the answer and order them to resolve it solo without instructional hints'
        },
        {
          ar: 'سحب المهام البرمجية المعقدة منه نهائياً وإسناد مهام إدخال بيانات روتينية بسيطة له طوال فترة عمله',
          en: 'Permanently reassign all complex tasks away from the junior, relegating them to trivial administrative work'
        }
      ];

      const allChoices = prng.shuffle([
        { ...correctAns, isCorrect: true },
        ...distractors.map(d => ({ ...d, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stim = makeSocialStimulus(
        'الموقف: لجأ إليك مطور مبتدئ يواجه صعوبة في حل خطأ برمجي معقد،',
        'وتستطيع أنت حل المشكلة في دقيقة واحدة بمجرد كتابة الكود بنفسك.',
        'Situation: A junior developer asks for guidance on a complex bug,',
        'which you could easily resolve yourself in one minute of coding.'
      );

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هو الأسلوب الإرشادي الأكثر فائدة على المدى الطويل لتطوير الكفاءات وبناء جيل هندسي متمكن؟`,
        questionText_en: `What is the most effective mentorship approach to foster autonomy and deep problem-solving skills?`,
        ...stim,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `الإرشاد الحقيقي (Socratic Mentoring) يركز على إكساب المتدرب منهجية التفكير وحل المشكلات بدلاً من تلقينه الإجابات الجاهزة.`,
        explanation_en: `Socratic mentoring develops independent troubleshooting capability rather than fostering ongoing cognitive dependency.`
      };
    }
  },

  // 15. Intellectual Property & Code Reuse Ethics
  {
    id: 'social_open_source_compliance',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const correctAns = {
        ar: 'فحص ترخيص المكتبة بدقة مع الفريق القانوني واستبدالها ببديل متوافق تجارياً لتفادي المخاطر القانونية',
        en: 'Audit the library license terms with legal counsel and adopt a commercially compliant alternative to mitigate risk'
      };
      const distractors = [
        {
          ar: 'دمج الكود سراً وتغيير أسماء الدوال والمتحولات لإخفاء مصدر البرمجية عن عمليات التدقيق التجاري',
          en: 'Integrate the code clandestinely, renaming identifiers to obscure origin from third-party IP audits'
        },
        {
          ar: 'تجاهل شروط التراخيص المفتوحة كلياً باعتبار أن الكود المتاح على الإنترنت مباح للاستخدام التجاري بلا قيود',
          en: 'Ignore license constraints entirely assuming code published on public repositories is free from commercial terms'
        },
        {
          ar: 'إلغاء تطوير الميزة البرمجية نهائياً والامتناع عن استخدام أي حلول برمجية مفتوحة المصدر في الشركة مستقبلاً',
          en: 'Scrap the feature entirely and prohibit the adoption of any open-source components across the firm indefinitely'
        }
      ];

      const allChoices = prng.shuffle([
        { ...correctAns, isCorrect: true },
        ...distractors.map(d => ({ ...d, isCorrect: false }))
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stim = makeSocialStimulus(
        'الموقف: وجد مطور مكتبة برمجية مفتوحة المصدر تحل مشكلة صعبة،',
        'لكن ترخيصها يفرض نشر كامل الكود المصدري للمنتج التجاري (GPL Copyleft).',
        'Situation: An engineer found an open-source library that solves an issue,',
        'but its viral license mandates disclosing all proprietary source code (GPL).'
      );

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هو الإجراء المؤسسي السليم لضمان سلامة الملكية الفكرية وحماية مصالح الشركة التجارية؟`,
        questionText_en: `What is the compliant corporate procedure to safeguard intellectual property against copyleft contamination?`,
        ...stim,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `الامتثال لتراخيص البرمجيات يحمي الشركة من الدعاوى القضائية الخطيرة ويستلزم اختيار مكتبات ذات تراخيص تجارية متوافقة (مثل MIT أو Apache).`,
        explanation_en: `Open-source compliance prevents severe legal exposure, ensuring only permissively licensed modules enter proprietary codebases.`
      };
    }
  }
];
